import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import { useGetCompanyById, useGetCompanyLogoSignedUrl } from "../../../api/company";
import { useGetCountriesData } from "../../../api/static-data";
import TailwindSideModal from "../../../components/commonModal/TailwindSideModal";
import TailwindBottomButtonView from "../../../components/mainBottomButtonView/TailwindBottomButtonView";
import CommonSplitContainer from "../../../components/ui/layout/CommonSplitContainer/CommonSplitContainer";
import CompanyFormLayout, { CompanySectionHeading } from "../shared/components/CompanyFormLayout";
import Field from "../shared/components/Field";

interface CompanyDetailsModalProps {
  visibleModal: boolean;
  id: string;
  setVisibleModal: () => void;
  setVisibleEdit: () => void;
}

/**
 * Tailwind rebuild of the legacy `CompanyDetailsModal`
 * (src/screens/company/companyDetails/CompanyDetailsModal.tsx), following
 * lcaSupplier's read-only `SupplierDetailsModal`: same shell as the new
 * Edit drawer, labelled `Field`s ("—" for empty) instead of disabled
 * inputs, and an "Edit company" shortcut like the other LCA details drawers.
 * The company is loaded through the same `useGetCompanyById` mutation each
 * time the drawer opens. Country, zip code, users, SKUs and subscription
 * date are shown too — they were editable but invisible in the legacy view.
 */
const CompanyDetailsModal = ({
  visibleModal,
  id,
  setVisibleModal,
  setVisibleEdit
}: CompanyDetailsModalProps) => {
  const {
    data: company,
    isLoading,
    mutate: loadCompany,
    reset: resetCompany
  } = useGetCompanyById();
  const { data: countriesData } = useGetCountriesData();

  useEffect(() => {
    if (visibleModal && id) {
      loadCompany(id);
    }
    return () => resetCompany();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleModal]);

  const { data: logoSignedUrl } = useGetCompanyLogoSignedUrl(company?.logoName);
  const statusLabel = company?.status
    ? company.status.charAt(0).toUpperCase() + company.status.slice(1).toLowerCase()
    : "";
  const countryName = countriesData?.find((c: any) => c.key === company?.tenantCountry)?.text;
  const currency = company?.currency
    ? [company.currency.country, company.currency.currency, company.currency.alphabeticCode]
        .filter(Boolean)
        .join(" - ")
    : "";
  const subscribedOn = company?.dateOfSubscription
    ? new Date(company.dateOfSubscription).toISOString().slice(0, 10)
    : "";

  return (
    <TailwindSideModal
      open={visibleModal}
      onOpenChange={open => {
        if (!open) setVisibleModal();
      }}
      eyebrow="Companies"
      title={company?.name || "Company details"}
      titleClassName="tw-font-display"
    >
      <CommonSplitContainer
        currentHeight={0.86}
        overflowX="hidden"
        topChild={
          isLoading ? (
            <div className="tw-flex tw-items-center tw-justify-center tw-py-24">
              <Loader2 className="tw-h-8 tw-w-8 tw-animate-spin tw-text-primary" />
            </div>
          ) : (
            <CompanyFormLayout
              main={
                <>
                  <section className="tw-flex tw-flex-col tw-gap-5">
                    <CompanySectionHeading>Company identity</CompanySectionHeading>
                    <div className="tw-grid tw-grid-cols-1 tw-gap-5 sm:tw-grid-cols-2">
                      <Field label="Company name" value={company?.name} />
                      <Field label="Registration number" value={company?.registrationNumber} mono />
                      <Field label="Company email" value={company?.companyEmail} />
                      <Field label="Phone" value={company?.phone} />
                      <Field label="Website" value={company?.website} />
                      <Field label="Industry" value={company?.industry} />
                    </div>
                  </section>

                  <section className="tw-flex tw-flex-col tw-gap-5">
                    <CompanySectionHeading>Location and subscription</CompanySectionHeading>
                    <Field label="Address" value={company?.address} />
                    <div className="tw-grid tw-grid-cols-1 tw-gap-5 sm:tw-grid-cols-2">
                      <Field label="Country" value={countryName} />
                      <Field label="Zip code" value={company?.zipCode} mono />
                      <Field label="Currency" value={currency} />
                      <Field label="Date of subscription" value={subscribedOn} mono />
                    </div>
                  </section>

                  <section className="tw-flex tw-flex-col tw-gap-5">
                    <CompanySectionHeading>Operating scale</CompanySectionHeading>
                    <div className="tw-grid tw-grid-cols-2 tw-gap-5 sm:tw-grid-cols-4">
                      <Field label="Employees" value={company?.noOfEmployees} mono />
                      <Field label="Users" value={company?.noOfUsers} mono />
                      <Field label="Products" value={company?.noOfProducts} mono />
                      <Field label="SKUs" value={company?.noOfSKUS} mono />
                    </div>
                  </section>
                </>
              }
              sidebar={
                <>
                  <CompanySectionHeading>Company record</CompanySectionHeading>
                  <div className="tw-flex tw-flex-col tw-gap-2">
                    <p className="tw-text-[10px] tw-font-medium tw-uppercase tw-tracking-wider tw-text-muted-foreground">
                      Company logo
                    </p>
                    <div className="tw-flex tw-h-32 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-dashed tw-border-border tw-bg-white tw-p-4">
                      {logoSignedUrl?.data ? (
                        <img
                          src={logoSignedUrl.data}
                          alt="Company logo"
                          className="tw-max-h-full tw-max-w-full tw-object-contain"
                        />
                      ) : (
                        <span className="tw-text-sm tw-text-muted-foreground">No logo</span>
                      )}
                    </div>
                  </div>
                  <Field label="Status" value={statusLabel} />
                </>
              }
            />
          )
        }
        bottomChild={
          <TailwindBottomButtonView
            cancelButtonStatus
            saveButtonStatus
            cancelButtonTitle="Close"
            saveTitle="Edit company"
            saveDisable={isLoading || !company}
            buttonSize="lg"
            cancelButton={setVisibleModal}
            saveButton={setVisibleEdit}
          />
        }
      />
    </TailwindSideModal>
  );
};

export default CompanyDetailsModal;
